// Chamadas do catálogo de serviços (áreas, sub-áreas, pendências) — um lugar só,
// usado pela aba do catálogo e pelo cadastro rápido de serviço das composições.
import axios from 'axios';
import { API_URL } from '../../config';
import { getToken } from './utils';
import { rotuloDaArea, type EscolhaArea, type Subarea } from '../../utils/areasEmpresa';

export type { EscolhaArea };

export type ServicoCatalogo = {
  id: string; codigo?: string | null; nome: string; descricao?: string | null;
  area?: string | null; unidade?: string | null; cnae?: string | null;
  setor_chave?: string | null; subarea_id?: string | null; subarea_nome?: string | null; ativo?: boolean;
};
export type AreaCatalogo = { chave: string; rotulo: string; n_servicos: number; subareas: Subarea[] };
export type Pendencia = {
  id: string; codigo?: string | null; nome: string; area?: string | null; unidade?: string | null;
  setor_chave?: string | null; subarea_id?: string | null; cnae?: string | null;
  falta: ('area' | 'subarea' | 'cnae')[]; sugestao: EscolhaArea; completa: boolean;
};
export type Pendencias = { total: number; sem_area: number; sem_subarea: number; sem_cnae: number; itens: Pendencia[] };

const corpo = (res: any) => (res?.data?.body ? JSON.parse(res.data.body) : res?.data);

// Mensagem do servidor quando houver; senão um texto que diz o que fazer.
export function erroDe(e: any, padrao = 'Não deu certo. Tente de novo.'): string {
  let c = e?.response?.data;
  if (c?.body) { try { c = JSON.parse(c.body); } catch { /* fica o envelope */ } }
  return c?.error || c?.erro || padrao;
}

async function cab() { return { headers: { Authorization: await getToken() } }; }

export async function listarServicos(): Promise<ServicoCatalogo[]> {
  const d = corpo(await axios.get(`${API_URL}/comercial/servicos-produtos`, await cab()));
  if (!Array.isArray(d)) throw new Error('resposta inesperada');
  return d;
}

// As 6 áreas com as sub-áreas.
// 🚨 Logo depois de uma publicação o servidor ANTIGO pode atender por alguns
// minutos: ele devolve `[{ nome, n_servicos }]`, sem `chave` nem `subareas`. Isso
// é tratado como FALHA, não como "sem áreas": com a lista vazia a tela deixaria
// salvar serviço sem sub-área e mostraria tudo como "sem área".
export async function listarAreas(): Promise<AreaCatalogo[]> {
  const d = corpo(await axios.get(`${API_URL}/comercial/areas`, await cab()));
  if (!Array.isArray(d) || d.length === 0 || d.some((a: any) => !a || !a.chave)) throw new Error('resposta inesperada');
  return d.map((a: any) => ({ ...a, subareas: Array.isArray(a.subareas) ? a.subareas : [] }));
}

async function acao<T>(action: string, dados: Record<string, unknown> = {}): Promise<T> {
  return corpo(await axios.post(`${API_URL}/comercial/servicos-produtos`, { action, ...dados }, await cab()));
}

export async function carregarPendencias(): Promise<Pendencias> {
  const d = await acao<Pendencias>('pendencias');
  if (!d || !Array.isArray(d.itens)) throw new Error('resposta inesperada');
  return d;
}

export async function aplicarAreas(itens: ({ id: string } & EscolhaArea)[]): Promise<{ aplicados: number }> {
  return acao('aplicar_areas', { itens });
}

// 🚨 O nome da sub-área vai em `nome_subarea`, NUNCA em `nome`: a rota é a mesma
// de criar serviço, e o servidor antigo (que não conhece `action`) criaria um
// SERVIÇO chamado "Batimetria" se recebesse `nome`. Sem `nome`, ele recusa.
export async function salvarSubarea(dados: { id?: string; setor_chave?: string; nome: string; ativo?: boolean }): Promise<Subarea> {
  const { nome, ...resto } = dados;
  const d = await acao<{ subarea: Subarea }>('subarea_salvar', { ...resto, nome_subarea: nome });
  if (!d?.subarea?.id) throw new Error('resposta inesperada');
  return d.subarea;
}

// O texto `area` vai junto com a chave de propósito: se o servidor antigo ainda
// atender o pedido, ele grava ao menos o nome da área em vez de deixar em branco.
type DadosServico = { codigo?: string | null; nome: string; unidade?: string; descricao?: string | null } & EscolhaArea;
const paraEnvio = (d: DadosServico) => ({ ...d, area: rotuloDaArea(d.setor_chave) || null });

export async function criarServico(d: DadosServico): Promise<ServicoCatalogo> {
  return corpo(await axios.post(`${API_URL}/comercial/servicos-produtos`, paraEnvio(d), await cab()));
}
export async function atualizarServico(id: string, d: DadosServico): Promise<ServicoCatalogo> {
  return corpo(await axios.put(`${API_URL}/comercial/servicos-produtos/${id}`, paraEnvio(d), await cab()));
}
export async function desativarServico(id: string): Promise<void> {
  await axios.delete(`${API_URL}/comercial/servicos-produtos/${id}`, await cab());
}
export async function regerarCodigos(): Promise<{ atualizados: number; areas: string[] }> {
  return corpo(await axios.post(`${API_URL}/comercial/servicos-produtos/regenerar-codigos`, {}, await cab()));
}
